import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'
import { ModalDialog } from '@/components/Modal'
import type { UnitId } from '@/annotations'

/**
 * The full-viewport overlay every modal screen actually renders — not a catalogued component
 * (see `@/components/Modal`'s header comment for why the split exists), so it lives here beside
 * `Refusals.tsx`, the other "screen-composition helper, not a catalogued component in its own
 * right."
 *
 * Focus trapping, `role="dialog"` (on `ModalDialog`), and Escape-to-dismiss are **not** in any
 * LLD-SPA-* rule or design/33-client-conventions.md §33 — no dialog-semantics rule exists
 * anywhere in the corpus for this family. This is ordinary WCAG dialog practice added because
 * building a modal without it would be a worse accessibility regression than the one Field.tsx
 * already fixed (§33.1), and is reported here as an addition rather than folded in silently.
 *
 * The scrim's background — Prototype_v2.html's `rgba(41,42,46,.44)` — is process/11-TOKEN-MAP.md's
 * own fold (line 156): identical to `--tx` at 44% over transparent, not a new mint.
 *
 * **Portalled to `document.body`, found live after `ShellLayer.tsx`'s own `<main>` became
 * `position:fixed` (the viewport-bounding fix).** `position:fixed` always creates a new stacking
 * context, regardless of `z-index` — every modal is rendered as a DOM descendant of `main` (a
 * screen conditionally renders one), so this scrim's own `z-index:80` was being resolved WITHIN
 * `main`'s local stacking context, not against `TopBar`'s `z-10` directly; `main` itself has no
 * `z-index` (auto), so its entire subtree — the modal's huge z-index included — painted at
 * `main`'s own (lower) level, behind the topbar. Screenshotted: the scrim didn't dim the topbar,
 * and a tall dialog would have been cut across by it. A portal is the standing fix for exactly
 * this class of problem — the modal's DOM position no longer has anything to do with which
 * ancestor's stacking context it inherits.
 *
 * `dataScr` goes on THIS scrim now, not a separate wrapper the caller renders around `<Modal>` —
 * that wrapper (every `m-*.tsx` screen had one) is what the portal turned into a real bug: once
 * `children` render into `document.body` instead of inline, the wrapper holding `data-scr` was
 * left an empty, zero-height div, structurally disconnected from `[data-cmp="modal"]` entirely
 * (`screenReach.mjs`'s own `[data-scr="m-cancel"] [data-cmp="modal"]` selector stopped matching
 * anything — found by `layout-audit.mjs` hanging on every modal screen). The scrim is still the
 * direct parent of `ModalDialog`'s own `data-cmp="modal"`, so putting `data-scr` here keeps that
 * exact selector correct without changing it. */
export function Modal({
  dataScr,
  labelledBy,
  onDismiss,
  maxWidth = 520,
  children,
}: {
  dataScr: UnitId
  labelledBy: string
  onDismiss: () => void
  maxWidth?: number
  children: ReactNode
}) {
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null
    const dialog = dialogRef.current
    const focusable = dialog?.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    focusable?.[0]?.focus()

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onDismiss()
        return
      }
      if (event.key !== 'Tab' || !dialog) return
      const nodes = dialog.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      if (nodes.length === 0) return
      const first = nodes[0]!
      const last = nodes[nodes.length - 1]!
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      previouslyFocused?.focus()
    }
  }, [onDismiss])

  return createPortal(
    <div
      data-scr={dataScr}
      className="fixed inset-0 py-10 px-9 overflow-auto"
      style={{ background: 'color-mix(in srgb, var(--tx) 44%, transparent)', zIndex: 80 }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onDismiss()
      }}
    >
      <ModalDialog ref={dialogRef} labelledBy={labelledBy} maxWidth={maxWidth}>
        {children}
      </ModalDialog>
    </div>,
    document.body,
  )
}
